import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Add, Delete, Sync as SyncIcon } from '@mui/icons-material';
import to from 'await-to-js';
import ApiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import { useCommonTranslation } from 'c-translation';
import { equals } from 'ramda';
import type {
    DatasourceColumn,
    DatasourceDetail,
    DatasourceListItem,
    DatasourceSyncRun,
} from 'c-sdk';
import { recentSyncs } from './datasourceSyncs';

type FormState = {
    name: string;
    description: string;
    query: string;
    source_connection: string;
    interval: string;
    is_paused: boolean;
    // Ordered — partition columns must be the trailing SELECT columns, in order.
    partition_columns: string[];
};

const EMPTY_FORM: FormState = {
    name: '',
    description: '',
    query: '',
    source_connection: 'uniled-athena',
    interval: '0 4 * * *',
    is_paused: false,
    partition_columns: [],
};

const detailToForm = (d: DatasourceDetail): FormState => ({
    name: d.name ?? '',
    description: d.description ?? '',
    query: d.query ?? '',
    source_connection: d.source_connection ?? 'uniled-athena',
    interval: d.interval ?? '',
    is_paused: Boolean(d.is_paused),
    partition_columns: d.partition_columns ?? [],
});

// The slug is the Athena table name; derive it from the name so the user doesn't manage it.
const slugify = (name: string): string =>
    name
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/^([^a-z])/, 'ds_$1') || 'datasource';

const emptyToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

const syncStatusColor = (status: DatasourceSyncRun['status']) =>
    status === 'succeeded' ? 'success' : status === 'failed' ? 'error' : 'warning';

export type DatasourceManagerHandle = {
    // Saves the open data source if it has unsaved edits. True when there is nothing left
    // unsaved; false (with the reason on screen) when it couldn't be saved.
    saveChanges: () => Promise<boolean>;
};

const DatasourceManager = forwardRef<DatasourceManagerHandle>(function DatasourceManager(_, ref) {
    const t = useCommonTranslation();
    const {
        start: loadList,
        data: listResp,
        isLoading: listLoading,
    } = useAPIClientRequest(ApiClient.Reports.datasourceList);
    const list = useMemo<DatasourceListItem[]>(
        () => (listResp as any)?.data?.data ?? [],
        [listResp],
    );

    const { start: readStart } = useAPIClientRequest(ApiClient.Reports.datasourceRead);
    const { start: detectStart, isLoading: detecting } = useAPIClientRequest(
        ApiClient.Reports.datasourceDetectColumns,
    );
    const { start: createStart, isLoading: creating } = useAPIClientRequest(
        ApiClient.Reports.datasourceCreate,
    );
    const { start: updateStart, isLoading: updating } = useAPIClientRequest(
        ApiClient.Reports.datasourceUpdate,
    );
    const { start: deleteStart } = useAPIClientRequest(ApiClient.Reports.datasourceDelete);
    const { start: syncStart, isLoading: syncing } = useAPIClientRequest(
        ApiClient.Reports.datasourceSync,
    );

    // null = nothing selected, 'new' = creating, number = editing that id.
    const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    // The open data source as last loaded or saved, so unsaved edits can be told apart.
    const [savedForm, setSavedForm] = useState<FormState>(EMPTY_FORM);
    const [columns, setColumns] = useState<DatasourceColumn[] | null>(null);
    const [syncs, setSyncs] = useState<DatasourceSyncRun[]>([]);
    const [notice, setNotice] = useState<{
        type: 'error' | 'success' | 'info';
        text: string;
    } | null>(null);

    useEffect(() => {
        loadList().catch(() => undefined);
    }, [loadList]);

    const isEditing = typeof selectedId === 'number';
    const patch = (p: Partial<FormState>) => setForm(current => ({ ...current, ...p }));

    // Partition-column ordering (order matters to Athena). Drag to reorder, like Pop Settings.
    const dragIndexRef = useRef<number | null>(null);
    const removeColAt = (index: number) =>
        setForm(current => ({
            ...current,
            partition_columns: current.partition_columns.filter((_, i) => i !== index),
        }));
    const moveCol = (from: number, to: number) =>
        setForm(current => {
            const next = [...current.partition_columns];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return { ...current, partition_columns: next };
        });

    const selectNew = () => {
        setSelectedId('new');
        setForm(EMPTY_FORM);
        setSavedForm(EMPTY_FORM);
        setColumns(null);
        setSyncs([]);
        setNotice(null);
    };

    const selectExisting = async (id: number) => {
        setSelectedId(id);
        setColumns(null);
        setNotice(null);
        const [err, res] = await to(readStart({ datasource: id, includeSyncs: true }));
        if (err) {
            setNotice({ type: 'error', text: err.message });
            return;
        }
        const detail = (res as any)?.data?.data as DatasourceDetail;
        setForm(detailToForm(detail));
        setSavedForm(detailToForm(detail));
        setSyncs(detail?.syncs ?? []);
    };

    const detectColumns = async () => {
        setNotice(null);
        const [err, res] = await to(
            detectStart({
                query: form.query,
                source_connection: form.source_connection,
                partition_columns: form.partition_columns,
            }),
        );
        if (err) {
            setColumns(null);
            setNotice({ type: 'error', text: err.message });
            return;
        }
        const body = (res as any)?.data;
        if (body?.success) {
            setColumns(body.columns ?? []);
            setNotice({ type: 'success', text: `Detected ${body.columns?.length ?? 0} columns.` });
        } else {
            setColumns(null);
            setNotice({ type: 'error', text: body?.message ?? 'Could not detect columns.' });
        }
    };

    const save = async (): Promise<boolean> => {
        setNotice(null);
        const common = {
            name: form.name,
            description: emptyToNull(form.description),
            query: form.query,
            source_connection: form.source_connection,
            interval: form.interval,
            is_paused: form.is_paused,
            partition_columns: form.partition_columns,
        };
        if (isEditing) {
            // slug is immutable, so it's not sent on update.
            const [err] = await to(updateStart({ datasource: selectedId as number, ...common }));
            if (err) {
                setNotice({ type: 'error', text: err.message });
                return false;
            }
            setSavedForm(form);
            setNotice({
                type: 'success',
                text: 'Data source updated. Sync to apply query changes.',
            });
            loadList().catch(() => undefined);
        } else {
            const [err, res] = await to(createStart({ ...common, slug: slugify(form.name) }));
            if (err) {
                setNotice({ type: 'error', text: err.message });
                return false;
            }
            setSavedForm(form);
            const created = (res as any)?.data?.data as DatasourceDetail;
            setNotice({
                type: 'success',
                text: 'Data source created. Run a sync to build the table before querying it.',
            });
            loadList().catch(() => undefined);
            if (created?.id != null) setSelectedId(created.id);
        }
        return true;
    };

    const runSync = async () => {
        if (!isEditing) return;
        setNotice(null);
        const [err, res] = await to(syncStart({ datasource: selectedId as number }));
        if (err) {
            setNotice({ type: 'error', text: err.message });
            return;
        }
        setNotice({
            type: 'info',
            text: (res as any)?.data?.message ?? 'Sync queued. It can take a few minutes.',
        });
        // Show the run just queued. Only the syncs are refreshed, so unsaved edits stay.
        const [readErr, readRes] = await to(
            readStart({ datasource: selectedId as number, includeSyncs: true }),
        );
        if (!readErr) setSyncs((readRes as any)?.data?.data?.syncs ?? []);
    };

    const remove = async () => {
        if (!isEditing) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm('Delete this data source definition? The built table is kept.')) return;
        const [err] = await to(deleteStart({ datasource: selectedId as number }));
        if (err) {
            setNotice({ type: 'error', text: err.message });
            return;
        }
        setSelectedId(null);
        setForm(EMPTY_FORM);
        loadList().catch(() => undefined);
    };

    const canSave =
        form.name.trim() !== '' && form.query.trim() !== '' && form.interval.trim() !== '';
    const isDirty = selectedId != null && !equals(form, savedForm);

    useImperativeHandle(ref, () => ({
        saveChanges: async () => {
            if (!isDirty) return true;
            if (!canSave) {
                setNotice({
                    type: 'error',
                    text: t('Pages.DashboardHome.custom.datasourceIncomplete'),
                });
                return false;
            }
            return save();
        },
    }));

    return (
        <Stack direction="row" gap={2} alignItems="stretch">
            <Box sx={{ width: 260, flexShrink: 0 }}>
                <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Add fontSize="small" />}
                    onClick={selectNew}
                    sx={{ mb: 1 }}
                >
                    New data source
                </Button>
                {listLoading && (
                    <Stack alignItems="center" sx={{ py: 2 }}>
                        <CircularProgress size={20} />
                    </Stack>
                )}
                <Stack gap={0.5}>
                    {list.map(ds => (
                        <Button
                            key={ds.id}
                            onClick={() => selectExisting(ds.id)}
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                border: th => `1px solid ${th.palette.divider}`,
                                borderRadius: 1,
                                bgcolor:
                                    ds.id === selectedId ? 'action.selected' : 'background.paper',
                                px: 1,
                                py: 0.75,
                            }}
                        >
                            <Stack sx={{ minWidth: 0, alignItems: 'flex-start' }}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 210 }}>
                                    {ds.name}
                                </Typography>
                                <Stack direction="row" gap={0.5} alignItems="center">
                                    <Chip
                                        size="small"
                                        label={ds.is_queryable ? 'ready' : 'not synced'}
                                        color={ds.is_queryable ? 'success' : 'default'}
                                        variant="outlined"
                                        sx={{ height: 18, fontSize: 10 }}
                                    />
                                    {ds.is_admin_only && (
                                        <Chip
                                            size="small"
                                            label="admin"
                                            variant="outlined"
                                            sx={{ height: 18, fontSize: 10 }}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        </Button>
                    ))}
                    {!listLoading && list.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                            No data sources yet.
                        </Typography>
                    )}
                </Stack>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ flex: 1, minWidth: 0 }}>
                {selectedId == null ? (
                    <Typography variant="body2" color="text.secondary">
                        Select a data source to edit, or create a new one. A data source is a saved
                        SQL query that is materialised into a queryable table on a schedule.
                    </Typography>
                ) : (
                    <Stack gap={2}>
                        {notice && (
                            <Alert severity={notice.type} onClose={() => setNotice(null)}>
                                {notice.text}
                            </Alert>
                        )}
                        <TextField
                            size="small"
                            label="Name"
                            fullWidth
                            value={form.name}
                            onChange={e => patch({ name: e.target.value })}
                        />
                        <TextField
                            size="small"
                            label="Description"
                            fullWidth
                            value={form.description}
                            onChange={e => patch({ description: e.target.value })}
                        />
                        <TextField
                            label="SQL query"
                            fullWidth
                            multiline
                            minRows={6}
                            value={form.query}
                            onChange={e => patch({ query: e.target.value })}
                            placeholder="SELECT campaign_id, owner_id, sum(plays) AS plays, play_date FROM …"
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        />
                        <Stack direction="row" gap={2}>
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <InputLabel>Source</InputLabel>
                                <Select
                                    label="Source"
                                    value={form.source_connection}
                                    onChange={e => patch({ source_connection: e.target.value })}
                                >
                                    <MenuItem value="uniled-athena">uniled-athena</MenuItem>
                                    <MenuItem value="mysql">mysql</MenuItem>
                                </Select>
                            </FormControl>
                            <Tooltip title="Cron expression, e.g. '0 4 * * *' for 4am daily.">
                                <TextField
                                    size="small"
                                    label="Schedule (cron)"
                                    sx={{ width: 180 }}
                                    value={form.interval}
                                    onChange={e => patch({ interval: e.target.value })}
                                />
                            </Tooltip>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.is_paused}
                                        onChange={(_, checked) => patch({ is_paused: checked })}
                                    />
                                }
                                label="Paused"
                            />
                        </Stack>
                        <Box>
                            <Autocomplete
                                multiple
                                freeSolo
                                size="small"
                                options={(columns ?? []).map(c => c.name)}
                                value={form.partition_columns}
                                onChange={(_, val) => patch({ partition_columns: val as string[] })}
                                renderTags={() => null}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="Partition columns"
                                        placeholder={
                                            form.partition_columns.length
                                                ? 'Add another…'
                                                : 'Optional — add columns'
                                        }
                                        helperText="Must be the trailing SELECT columns, in order. Drag the pills to reorder."
                                    />
                                )}
                            />
                            {form.partition_columns.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                    {form.partition_columns.map((col, index) => (
                                        <Chip
                                            key={col}
                                            size="small"
                                            color="primary"
                                            label={`${index + 1}: ${col}`}
                                            onDelete={() => removeColAt(index)}
                                            draggable
                                            onDragStart={() => {
                                                dragIndexRef.current = index;
                                            }}
                                            onDragOver={(e: React.DragEvent) => e.preventDefault()}
                                            onDrop={() => {
                                                const from = dragIndexRef.current;
                                                dragIndexRef.current = null;
                                                if (from == null) return;
                                                moveCol(from, index);
                                            }}
                                            sx={{ cursor: 'grab' }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <Divider />

                        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                            <Button
                                variant="outlined"
                                onClick={detectColumns}
                                disabled={detecting || form.query.trim() === ''}
                            >
                                {detecting ? 'Detecting…' : 'Detect columns'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={save}
                                disabled={!canSave || creating || updating}
                            >
                                {isEditing ? 'Save changes' : 'Create'}
                            </Button>
                            {isEditing && (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={<SyncIcon fontSize="small" />}
                                        onClick={runSync}
                                        disabled={syncing}
                                    >
                                        Sync now
                                    </Button>
                                    <Box sx={{ flex: 1 }} />
                                    <IconButton color="error" onClick={remove}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </>
                            )}
                        </Stack>

                        {columns && columns.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Detected columns
                                </Typography>
                                <Stack direction="row" gap={0.5} flexWrap="wrap">
                                    {columns.map(c => (
                                        <Chip
                                            key={c.name}
                                            size="small"
                                            label={`${c.name} · ${c.type}`}
                                            variant="outlined"
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {isEditing && syncs.length > 0 && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Recent syncs
                                </Typography>
                                <Stack gap={0.5}>
                                    {recentSyncs(syncs).map(run => (
                                        <Stack
                                            key={run.id}
                                            direction="row"
                                            gap={1}
                                            alignItems="center"
                                        >
                                            <Chip
                                                size="small"
                                                label={run.status}
                                                color={syncStatusColor(run.status)}
                                                variant="outlined"
                                                sx={{ height: 20 }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {run.run_type}
                                                {run.row_count != null
                                                    ? ` · ${run.row_count.toLocaleString()} rows`
                                                    : ''}
                                                {run.started_at ? ` · ${run.started_at}` : ''}
                                            </Typography>
                                            {run.error_message && (
                                                <Typography variant="caption" color="error" noWrap>
                                                    {run.error_message}
                                                </Typography>
                                            )}
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                )}
            </Box>
        </Stack>
    );
});

export default DatasourceManager;
